import { v4 as uuid } from 'uuid';
import { AccountInterface, RpcProvider } from 'starknet';
import {
  getNostraContract,
  getNostraTokenFromFlags,
  getAccountAddressFromId,
  getTokenAmountTransferredInReceipt,
  waitForTransactionReceipt,
} from '../../utils';
import { Decimal } from '../../datastructures';
import { Asset, InvokeResponse } from '../../interfaces';
import { Nullable } from '../../interfaces';
import { DecimalFormat } from '@tempusfinance/decimal';
import { addToast } from '../toast';
import { createAsyncAtom, emitAtomEvent } from '../../lib/state';
import { pauseBalanceRefresh, resumeBalanceRefresh } from './pauseBalanceRefresh';
import { withdrawInput } from './lendAndBorrowInputAtom';

interface WithdrawRequest {
  asset: Asset;
  amount: Decimal; // Amount that is passed to contract - in case user wants to withdraw everything, this is set to MAX decimal.
  inputAmount: Decimal; // Amount that user entered in input field
  to: string;
  accountId: number;
  isInterestBearing: boolean;
  isCollateral: boolean;
  txnId: string;
  provider: RpcProvider;
}

interface WithdrawStatus {
  pending: boolean;
  request: WithdrawRequest;
  success?: boolean;
  error?: Error;
  getTransactionResponse?: InvokeResponse;
  transactionData?: {
    withdrawnAmount: Decimal;
  };
  txnId: string;
}

/* interface WithdrawResponse {
  request: WithdrawRequest;
  getTransactionResponse?: InvokeTransactionResponse | void;
  getTransactionReceipt?: InvokeTransactionReceiptResponse;
  transactionData?: {
    withdrawnAmount: Decimal;
    gasFee: Decimal;
  };
  error?: Error;
  txnId: string;
} */

const lendAndBorrowWithdrawStatusAtom = createAsyncAtom<Nullable<WithdrawStatus>>(async () => null);

const lendAndBorrowWithdraw = async (walletAccount: AccountInterface, request: WithdrawRequest) => {
  const { asset, amount, inputAmount, accountId, isInterestBearing, isCollateral, txnId, provider } = request;

  const nostraToken = getNostraTokenFromFlags(asset, {
    interestBearing: isInterestBearing,
    collateral: isCollateral,
  });

  const nostraTokenContract = getNostraContract(asset, nostraToken.address, walletAccount);

  if (!nostraTokenContract) {
    return null;
  }

  const accountAddress = getAccountAddressFromId(walletAccount.address, accountId);

  const amountFormatted = DecimalFormat.format(inputAmount, {
    style: 'currency',
    currency: asset.token,
    fractionDigits: asset.uiTokenPrecision,
    pad: true,
    lessThanFormat: true,
  });

  pauseBalanceRefresh();
  try {
    lendAndBorrowWithdrawStatusAtom.setValue({
      pending: true,
      request,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await nostraTokenContract.burn(amount, inputAmount, walletAccount, accountAddress);
    } catch (error) {
      lendAndBorrowWithdrawStatusAtom.setValue({
        pending: false,
        success: false,
        error: new Error('Failed to withdraw!'),
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Withdraw ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Withdraw ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      const userRejectError = new Error('Rejected by user.');
      lendAndBorrowWithdrawStatusAtom.setValue({
        pending: false,
        success: false,
        error: userRejectError,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Withdraw ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      const receiptFetchFailed = new Error('Failed to fetch withdraw transaction receipt!');
      lendAndBorrowWithdrawStatusAtom.setValue({
        pending: false,
        success: false,
        error: receiptFetchFailed,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Withdraw ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      const txnReverted = new Error('Transaction reverted!');
      lendAndBorrowWithdrawStatusAtom.setValue({
        pending: false,
        success: false,
        error: txnReverted,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Withdraw ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    if (transactionReceipt.isSuccess()) {
      const withdrawnAmount = getTokenAmountTransferredInReceipt(transactionReceipt, request.asset);

      lendAndBorrowWithdrawStatusAtom.setValue({
        pending: false,
        success: true,
        request,
        getTransactionResponse: transactionResponse,
        transactionData: {
          withdrawnAmount: withdrawnAmount,
        },
        txnId,
      });
    }

    emitAtomEvent('lend-and-borrow-withdraw');

    withdrawInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Withdrew **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

export { lendAndBorrowWithdrawStatusAtom, lendAndBorrowWithdraw };
